import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "locales";
import { generatePath } from "react-router-dom";
import useAuthUser from "hooks/useAuthUser";
import { SYSTEM_MESSAGE_PREFIX } from "lib/emitter";
import providers from "lib/providers";
import { Routes } from "router/config";
import BaseSpinner from "components/BaseSpinner";
import "./style.scss";

function PageJoinGroup() {
  const { t } = useTranslation();
  const history = useHistory();
  const { groupId } = useParams<{ groupId: string }>();
  const { user } = useAuthUser();
  const [status, setStatus] = useState<"loading" | "success" | "error" | "not_found">("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!user) {
      history.replace("/login");
      return;
    }
    if (!groupId) {
      setStatus("not_found");
      return;
    }

    (async () => {
      try {
        // Invite links now carry the unguessable slug; resolve by slug first,
        // falling back to a numeric-id search so older numeric links still work.
        let group = /^\d+$/.test(groupId)
          ? null
          : await providers.GroupsProvider.findBySlug(groupId).catch(() => null);
        if (!group) {
          const groups = await providers.GroupsProvider.search({ data: { id: groupId } });
          group = groups && groups.length > 0 ? groups[0] : null;
        }
        if (!group) {
          setStatus("not_found");
          return;
        }
        if (!group.isPublic) {
          setErrorMsg(t("This group is not public"));
          setStatus("error");
          return;
        }
        // Check if already a member
        const isMember = group.memberIds?.includes(String(user.id));
        if (isMember) {
          history.replace(generatePath(Routes.groupMessages, { groupId: group.slug || groupId }));
          return;
        }
        // Join the group
        await providers.GroupsProvider.joinPublicGroup(group.id);
        // Send system message
        try {
          const threads = await providers.MessageThreadsProvider.find({
            data: { userId: String(user.id), subjectType: "group", subjectId: group.id, size: 1 },
          });
          if (threads.result?.length > 0) {
            await providers.MessagesProvider.sendExtendedMessages({
              data: { text: `${SYSTEM_MESSAGE_PREFIX}${user.screenName || "User"} joined the group`, threadIds: [threads.result[0].id] },
            });
          }
        } catch (e) { console.warn("Failed to send system message:", e); }
        setStatus("success");
        setTimeout(() => {
          history.replace(generatePath(Routes.groupMessages, { groupId: group.slug || groupId }));
        }, 500);
      } catch (e: any) {
        console.error("Failed to join group:", e);
        setErrorMsg(t("Failed to join group"));
        setStatus("error");
      }
    })();
  }, [groupId, user, history, t]);

  return (
    <div className="page-join-group">
      {status === "loading" && (
        <div className="page-join-group__loading">
          <BaseSpinner />
          <p>{t("Joining group...")}</p>
        </div>
      )}
      {status === "success" && (
        <div className="page-join-group__success">
          <p>{t("Successfully joined! Redirecting...")}</p>
        </div>
      )}
      {status === "not_found" && (
        <div className="page-join-group__error">
          <p>{t("Group not found")}</p>
          <button type="button" onClick={() => history.push("/messages")}>
            {t("Go to Messages")}
          </button>
        </div>
      )}
      {status === "error" && (
        <div className="page-join-group__error">
          <p>{errorMsg}</p>
          <button type="button" onClick={() => history.push("/messages")}>
            {t("Go to Messages")}
          </button>
        </div>
      )}
    </div>
  );
}

export default PageJoinGroup;
